/**
 * PointCloudUploadPanel — rechte Sidebar im Viewer.
 * Oben: Drag-and-Drop-Kachel zum Hochladen von e57/LAS-Dateien.
 *       Workflow wird automatisch über die fileTypeMappings des
 *       Projekt-ETL-Servers anhand der Dateiendung bestimmt.
 * Darunter: Kacheln aller Pointclouds des aktuellen Projekts.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { Upload, FileUp, Cloud, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react'
import { apiFetch, API_URL } from '../../lib/apiBase'
import { useAuthStore } from '../../store/auth'

interface FileTypeMapping {
  extension: string
  workflowId: string
  workflowName?: string
}
interface EtlServerInfo {
  id: string
  name: string
  apiUrl: string
  isDefault: boolean
  fileTypeMappings?: FileTypeMapping[]
}

type UploadPhase = 'idle' | 'uploading' | 'starting' | 'running' | 'done' | 'error'

interface UploadState {
  fileName: string
  fileSize: number
  phase: UploadPhase
  progress: number          // 0..100
  jobId?: string
  jobStatus?: string
  error?: string
}

function fileExt(name: string) {
  const m = name.match(/\.([a-z0-9]+)$/i)
  return m ? m[1].toLowerCase() : ''
}

function fmtBytes(n: number) {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`
}

export function PointCloudUploadPanel() {
  const currentProject = useAuthStore((s) => s.currentProject)
  const projectId = currentProject?.id
  const etlServer = (currentProject as any)?.etlServer as EtlServerInfo | null | undefined
  const pointClouds = (currentProject?.pointClouds || []) as Array<{ id: string; name: string; path?: string; localPath?: string; url?: string; panoramaUrl?: string }>

  const [uploads, setUploads] = useState<UploadState[]>([])
  const [dragActive, setDragActive] = useState(false)
  const [rejectedFiles, setRejectedFiles] = useState<string[]>([])

  // Akzeptierte Endungen ergeben sich ausschließlich aus den fileTypeMappings
  // des dem Projekt zugewiesenen ETL-Servers.
  const acceptedExt = ((etlServer?.fileTypeMappings || [])
    .map((m) => (m.extension || '').toLowerCase().trim().replace(/^\./, ''))
    .filter(Boolean))
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const pollTimers = useRef<Record<string, number>>({})

  useEffect(() => () => {
    for (const id of Object.keys(pollTimers.current)) window.clearTimeout(pollTimers.current[id])
  }, [])

  const updateUpload = useCallback((fileName: string, patch: Partial<UploadState>) => {
    setUploads((list) => list.map((u) => (u.fileName === fileName ? { ...u, ...patch } : u)))
  }, [])

  const pollJob = useCallback(async (fileName: string, jobId: string) => {
    if (!projectId) return
    try {
      const r = await apiFetch(`/api/projects/${projectId}/etl/jobs/${jobId}`)
      const json = await r.json()
      const job = json.data || json
      updateUpload(fileName, { jobStatus: job.status })
      if (job.status === 'succeeded') {
        updateUpload(fileName, { phase: 'done' })
        return
      }
      if (job.status === 'failed' || job.status === 'cancelled') {
        updateUpload(fileName, { phase: 'error', error: job.error || `Job ${job.status}` })
        return
      }
      pollTimers.current[fileName] = window.setTimeout(() => pollJob(fileName, jobId), 3000)
    } catch (e: any) {
      updateUpload(fileName, { phase: 'error', error: e?.message || 'Status-Abfrage fehlgeschlagen' })
    }
  }, [projectId, updateUpload])

  const startUpload = useCallback(async (file: File) => {
    if (!projectId) return
    if (!etlServer) {
      setUploads((list) => [...list, {
        fileName: file.name, fileSize: file.size, phase: 'error', progress: 0,
        error: 'Kein ETL-Server für dieses Projekt zugewiesen',
      }])
      return
    }

    const ext = fileExt(file.name)
    const mapping = (etlServer.fileTypeMappings || []).find((m) => m.extension.toLowerCase() === ext)
    if (!mapping) {
      setUploads((list) => [...list, {
        fileName: file.name, fileSize: file.size, phase: 'error', progress: 0,
        error: `Keine Workflow-Zuordnung für ".${ext}" auf ETL-Server "${etlServer.name}" konfiguriert`,
      }])
      return
    }

    setUploads((list) => [...list, { fileName: file.name, fileSize: file.size, phase: 'uploading', progress: 0 }])

    try {
      // Upload + Job-Start in einem Schritt: orto-api-Proxy macht TUS und finalize
      // mit workflowIds → ETL-Server startet die passenden Jobs automatisch.
      const result = await new Promise<any>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        const params = new URLSearchParams({
          filename: file.name,
          workflowIds: mapping.workflowId,
        })
        const url = `${API_URL}/api/projects/${projectId}/etl/upload?${params.toString()}`
        xhr.open('POST', url, true)
        xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('accessToken') || ''}`)
        xhr.setRequestHeader('Content-Type', 'application/octet-stream')
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) updateUpload(file.name, { progress: Math.round((ev.loaded / ev.total) * 100) })
        }
        xhr.onload = () => {
          try {
            const j = JSON.parse(xhr.responseText)
            if (xhr.status >= 200 && xhr.status < 300) resolve(j.data || j)
            else reject(new Error(j?.error?.message || `HTTP ${xhr.status}`))
          } catch { reject(new Error(`HTTP ${xhr.status}`)) }
        }
        xhr.onerror = () => reject(new Error('Netzwerkfehler beim Upload'))
        xhr.send(file)
      })

      const startedJobIds: string[] = Array.isArray(result?.startedJobIds) ? result.startedJobIds : []
      if (startedJobIds.length === 0) {
        // Datei wurde hochgeladen, aber kein Job gestartet — z.B. MediaType-Mismatch
        throw new Error(`ETL-Server hat keinen Job gestartet (Datei akzeptiert als "${result?.mediaTypeId || 'unbekannt'}", Workflow "${mapping.workflowName || mapping.workflowId}" akzeptiert evtl. anderen Typ)`)
      }

      const jobId = startedJobIds[0]
      updateUpload(file.name, { phase: 'running', jobId, jobStatus: 'queued' })
      pollJob(file.name, jobId)
    } catch (e: any) {
      updateUpload(file.name, { phase: 'error', error: e?.message || 'Upload fehlgeschlagen' })
    }
  }, [projectId, etlServer, updateUpload, pollJob])

  // Filtert Dateien anhand der erlaubten Endungen und merkt sich abgelehnte Namen
  // zur kurzen Anzeige in der Drop-Zone.
  const acceptOrReject = (files: File[]): File[] => {
    if (acceptedExt.length === 0) {
      setRejectedFiles(files.map((f) => f.name))
      return []
    }
    const accepted: File[] = []
    const rejected: string[] = []
    for (const f of files) {
      if (acceptedExt.includes(fileExt(f.name))) accepted.push(f)
      else rejected.push(f.name)
    }
    setRejectedFiles(rejected)
    if (rejected.length > 0) window.setTimeout(() => setRejectedFiles([]), 6000)
    return accepted
  }

  // ── Drag-and-Drop-Handler ────────────────────────────────────────────
  const onDragOver = (e: React.DragEvent) => { e.preventDefault(); setDragActive(true) }
  const onDragLeave = (e: React.DragEvent) => { e.preventDefault(); setDragActive(false) }
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDragActive(false)
    const files = Array.from(e.dataTransfer.files || [])
    acceptOrReject(files).forEach((f) => startUpload(f))
  }
  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    acceptOrReject(files).forEach((f) => startUpload(f))
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  if (!projectId) {
    return <div style={{ padding: 16, color: '#888' }}>Kein Projekt geladen.</div>
  }

  const tileBase: React.CSSProperties = {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  }

  return (
    <div style={{ padding: '8px 8px 16px', overflow: 'auto', height: '100%' }}>
      {/* ── Drop-Zone ─────────────────────────────────────────────── */}
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{
          ...tileBase,
          border: `2px dashed ${dragActive ? '#1a1a1a' : '#cbd5e1'}`,
          background: dragActive ? '#f1f5f9' : '#f8fafc',
          textAlign: 'center',
          padding: 24,
          cursor: 'pointer',
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptedExt.length ? acceptedExt.map((e) => '.' + e).join(',') : undefined}
          disabled={acceptedExt.length === 0}
          onChange={onPick}
          style={{ display: 'none' }}
        />
        <Upload size={28} strokeWidth={1.5} style={{ color: '#475569', margin: '0 auto 8px' }} />
        <p style={{ margin: 0, fontWeight: 500, fontSize: 13, color: '#1f2937' }}>
          Datei hierher ziehen
        </p>
        <p style={{ margin: '4px 0 0', fontSize: 11, color: '#64748b' }}>
          {acceptedExt.length > 0
            ? `oder klicken zum Auswählen (${acceptedExt.join(', ')})`
            : 'Upload deaktiviert'}
        </p>
        {!etlServer && (
          <p style={{ margin: '8px 0 0', fontSize: 11, color: '#dc2626' }}>
            Kein ETL-Server für dieses Projekt zugewiesen
          </p>
        )}
        {etlServer && acceptedExt.length === 0 && (
          <p style={{ margin: '8px 0 0', fontSize: 11, color: '#d97706' }}>
            Keine Datei-Typ-Zuordnungen im ETL-Server "{etlServer.name}" konfiguriert
          </p>
        )}
        {rejectedFiles.length > 0 && (
          <p style={{ margin: '8px 0 0', fontSize: 11, color: '#dc2626' }}>
            Nicht erlaubt: {rejectedFiles.slice(0, 3).join(', ')}
            {rejectedFiles.length > 3 && ` … (${rejectedFiles.length - 3} weitere)`}
          </p>
        )}
      </div>

      {/* ── Aktive/abgeschlossene Uploads ─────────────────────────── */}
      {uploads.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <h4 style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, margin: '12px 4px 6px' }}>
            Uploads
          </h4>
          {uploads.map((u, i) => (
            <div key={i} style={tileBase}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {u.phase === 'done' && <CheckCircle2 size={16} color="#16a34a" />}
                {u.phase === 'error' && <AlertCircle size={16} color="#dc2626" />}
                {(u.phase === 'uploading' || u.phase === 'starting' || u.phase === 'running') && (
                  <Loader2 size={16} color="#475569" style={{ animation: 'spin 1s linear infinite' }} />
                )}
                {u.phase === 'idle' && <FileUp size={16} color="#64748b" />}
                <span style={{ fontSize: 13, fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {u.fileName}
                </span>
                <span style={{ fontSize: 11, color: '#64748b' }}>{fmtBytes(u.fileSize)}</span>
              </div>
              {u.phase === 'uploading' && (
                <div style={{ marginTop: 6 }}>
                  <div style={{ background: '#e2e8f0', borderRadius: 4, height: 4, overflow: 'hidden' }}>
                    <div style={{ background: '#1a1a1a', height: '100%', width: `${u.progress}%`, transition: 'width 0.2s' }} />
                  </div>
                  <p style={{ fontSize: 11, color: '#64748b', textAlign: 'right', margin: '4px 0 0' }}>{u.progress}%</p>
                </div>
              )}
              {u.phase === 'starting' && (
                <p style={{ fontSize: 11, color: '#64748b', margin: '6px 0 0' }}>Job wird gestartet …</p>
              )}
              {u.phase === 'running' && (
                <p style={{ fontSize: 11, color: '#64748b', margin: '6px 0 0' }}>
                  Konvertierung: <strong>{u.jobStatus || 'läuft'}</strong>
                </p>
              )}
              {u.phase === 'done' && (
                <p style={{ fontSize: 11, color: '#16a34a', margin: '6px 0 0' }}>Konvertierung abgeschlossen</p>
              )}
              {u.phase === 'error' && (
                <p style={{ fontSize: 11, color: '#dc2626', margin: '6px 0 0' }}>{u.error}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Pointclouds des Projekts ──────────────────────────────── */}
      <h4 style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, margin: '12px 4px 6px' }}>
        Pointclouds ({pointClouds.length})
      </h4>
      {pointClouds.length === 0 ? (
        <p style={{ fontSize: 12, color: '#64748b', padding: '8px 4px' }}>Noch keine Pointclouds im Projekt.</p>
      ) : (
        pointClouds.map((pc) => (
          <div key={pc.id} style={tileBase}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Cloud size={16} color="#475569" />
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {pc.name}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>
                  {pc.id}
                </p>
              </div>
            </div>
            {pc.path && (
              <p style={{ margin: '6px 0 0', fontSize: 10, color: '#64748b', wordBreak: 'break-all', fontFamily: 'monospace' }}>
                {pc.path}
              </p>
            )}
          </div>
        ))
      )}

      {/* Inline-Keyframes für Loader-Animation */}
      <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }`}</style>
    </div>
  )
}
